/**
 * Apresentação centralizada do cronômetro e do prazo operacional da OS.
 *
 * RELEASE: 2026-10-09 v1.2.0 - Centraliza o cálculo visual do progresso da fase.
 * RELEASE: 2026-10-09 v1.1.0 - Formatação compacta e tom semântico do badge de prazo.
 * RELEASE: 2026-10-09 v1.0.0 - Formatação inicial compartilhada entre lista,
 *          Kanban e modal de detalhes.
 */
export const SERVICE_ORDER_DEADLINE_OPTIONS = [
  { value: '10d', label: '10 dias' },
  { value: '15d', label: '15 dias' },
  { value: '25d', label: '25 dias' },
  { value: '30d', label: '30 dias' },
  { value: '45d', label: '45 dias' },
  { value: '3m', label: '3 meses' },
  { value: '6m', label: '6 meses' },
  { value: '1y', label: '1 ano' },
  { value: 'gt1y', label: 'Mais de 1 ano' },
] as const;

export type ServiceOrderDeadlineBucket = typeof SERVICE_ORDER_DEADLINE_OPTIONS[number]['value'];

/** Calcula o avanço visual da fase, com mínimo visível e limite de 100%. */
export function calculateServiceOrderPhaseProgress(position: number, total: number): number {
  if (total <= 0) return 10;
  return Math.min(100, Math.max(10, (Math.max(1, position) / total) * 100));
}

/** Formata segundos sem alterar a largura da célula durante a atualização. */
export function formatServiceOrderDuration(seconds: number | null | undefined): string {
  const safe = Math.max(0, Math.floor(seconds ?? 0));
  const days = Math.floor(safe / 86400);
  const hours = Math.floor((safe % 86400) / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  if (days > 0) return `${days}d ${hours}h`;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/** Define o tom semântico do prazo corrido para leitura rápida. */
export function serviceOrderDeadlineClass(seconds: number): string {
  if (seconds >= 30 * 86400) return 'bg-destructive/15 text-destructive';
  if (seconds >= 7 * 86400) return 'bg-warning/15 text-warning-foreground';
  return 'bg-success/15 text-success';
}

/** Converte o prazo corrido para o formato compacto usado no badge. */
export function formatServiceOrderDeadline(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  if (safe < 86400) return `${Math.max(1, Math.floor(safe / 3600))}h`;
  const days = Math.floor(safe / 86400);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}m`;
  return `${Math.floor(months / 12)}a`;
}